import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Calendar, Plus, Trash2 } from "lucide-react";
import { brl } from "@/api";
import CurrencyInput from "@/components/CurrencyInput";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────────────────────
// Modal: criar/editar Plano de Parcelamento (Pendente Programado)
// `totalAmount` é fixo (passado pelo pai). Permite alternar entre "parcelas
// iguais" e "personalizadas". Valida que a soma bate com totalAmount.
// Se `initialPlan` for fornecido, abre em modo de edição com aquele plano.
//
// Callbacks:
//   onSave(planPayload) → { kind, total_amount, installments[] }
//   onCancel()
// ─────────────────────────────────────────────────────────────────────────────
const todayISO = () => new Date().toISOString().slice(0, 10);
const addInterval = (iso, units, periodicity) => {
  const d = new Date(iso + "T12:00:00");
  if (periodicity === "weekly") d.setDate(d.getDate() + 7 * units);
  else if (periodicity === "biweekly") d.setDate(d.getDate() + 14 * units);
  else d.setMonth(d.getMonth() + units); // monthly (default)
  return d.toISOString().slice(0, 10);
};

export default function InstallmentPlanModal({
  open, onClose, totalAmount, initialPlan, onSave, customerName,
}) {
  const [kind, setKind] = useState("equal"); // equal | custom
  const [count, setCount] = useState(2);
  const [countStr, setCountStr] = useState("2");
  const [firstDate, setFirstDate] = useState(todayISO());
  const [customList, setCustomList] = useState([]);
  const [saving, setSaving] = useState(false);
  // Periodicidade: weekly | biweekly | monthly. Default = monthly.
  const [periodicity, setPeriodicity] = useState("monthly");

  // Inicializa quando abre
  useEffect(() => {
    if (!open) return;
    if (initialPlan) {
      setKind(initialPlan.kind || "equal");
      const c = initialPlan.installments?.length || 2;
      setCount(c);
      setCountStr(String(c));
      setFirstDate(initialPlan.installments?.[0]?.due_date || todayISO());
      setCustomList((initialPlan.installments || []).map(p => ({
        due_date: p.due_date, amount: Number(p.amount || 0),
      })));
      setPeriodicity(initialPlan.periodicity || "monthly");
    } else {
      setKind("equal");
      setCount(2);
      setCountStr("2");
      setFirstDate(todayISO());
      setCustomList([]);
      setPeriodicity("monthly");
    }
  }, [open, initialPlan]);

  // Quando muda para custom pela primeira vez ou muda count/periodicidade,
  // popula a lista respeitando o intervalo configurado.
  useEffect(() => {
    if (kind !== "custom") return;
    if (customList.length === count) return;
    const next = [];
    for (let i = 0; i < count; i++) {
      next.push(customList[i] || {
        due_date: addInterval(firstDate, i, periodicity),
        amount: i === count - 1
          ? Math.max(0, (totalAmount || 0) - (count - 1) * Math.floor((totalAmount || 0) / count * 100) / 100)
          : Math.floor((totalAmount || 0) / count * 100) / 100,
      });
    }
    setCustomList(next);
    // eslint-disable-next-line
  }, [kind, count]);

  // Lista "computada" para parcelas iguais — respeita periodicidade
  const equalList = useMemo(() => {
    if (!totalAmount || count < 1) return [];
    const list = [];
    const base = Math.floor((totalAmount / count) * 100) / 100;
    let remaining = +(totalAmount).toFixed(2);
    for (let i = 0; i < count; i++) {
      const isLast = i === count - 1;
      const amt = isLast ? +(remaining).toFixed(2) : base;
      list.push({ due_date: addInterval(firstDate, i, periodicity), amount: amt });
      remaining -= amt;
    }
    return list;
  }, [totalAmount, count, firstDate, periodicity]);

  // Quando o usuário troca a periodicidade no modo CUSTOM, re-distribui as
  // datas a partir da primeira (mantém valores). Sem isso, a UX fica estranha.
  useEffect(() => {
    if (kind !== "custom") return;
    if (!customList.length) return;
    setCustomList(prev => prev.map((p, i) => ({
      ...p, due_date: i === 0 ? p.due_date : addInterval(prev[0].due_date, i, periodicity),
    })));
    // eslint-disable-next-line
  }, [periodicity]);

  const finalList = kind === "equal" ? equalList : customList;
  const sumAmount = useMemo(
    () => finalList.reduce((s, p) => s + Number(p.amount || 0), 0),
    [finalList]
  );
  const sumOk = Math.abs(sumAmount - (totalAmount || 0)) < 0.05;

  const updateCustom = (idx, patch) => {
    setCustomList(prev => prev.map((p, i) => i === idx ? { ...p, ...patch } : p));
  };
  const addCustomLine = () => {
    if (customList.length >= 12) return;
    const lastDate = customList[customList.length - 1]?.due_date || firstDate;
    setCustomList(prev => [...prev, { due_date: addInterval(lastDate, 1, periodicity), amount: 0 }]);
    setCount(c => c + 1);
  };
  const removeCustomLine = (idx) => {
    setCustomList(prev => prev.filter((_, i) => i !== idx));
    setCount(c => Math.max(1, c - 1));
  };

  const submit = async () => {
    if (!finalList.length) return toast.error("Adicione ao menos uma parcela");
    if (finalList.some(p => !p.due_date)) return toast.error("Todas as parcelas precisam de data");
    if (finalList.some(p => !p.amount || p.amount <= 0)) return toast.error("Todas as parcelas precisam de valor > 0");
    if (!sumOk) return toast.error(`Soma das parcelas (${brl(sumAmount)}) deve bater com ${brl(totalAmount)}`);
    setSaving(true);
    try {
      await onSave({
        kind,
        periodicity,
        total_amount: Number((totalAmount || 0).toFixed(2)),
        installments: finalList.map(p => ({
          due_date: p.due_date,
          amount: Number(Number(p.amount).toFixed(2)),
        })),
      });
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Erro ao salvar parcelamento");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="installment-plan-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Calendar size={16}/> Crediário {customerName ? `· ${customerName}` : ""}
          </DialogTitle>
        </DialogHeader>

        <div className="text-xs text-neutral-500 -mt-1 mb-1">
          Valor pendente: <span className="font-medium text-neutral-900 dark:text-neutral-100">{brl(totalAmount)}</span>
        </div>

        <div className="flex gap-2" data-testid="plan-kind-tabs">
          <button
            type="button" onClick={() => setKind("equal")}
            className={`flex-1 text-sm py-2 rounded-md border ${kind === "equal" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] dark:border-white/10"}`}
            data-testid="plan-kind-equal"
          >Parcelas iguais</button>
          <button
            type="button" onClick={() => setKind("custom")}
            className={`flex-1 text-sm py-2 rounded-md border ${kind === "custom" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#E5E5E5] dark:border-white/10"}`}
            data-testid="plan-kind-custom"
          >Personalizadas</button>
        </div>

        {/* Periodicidade */}
        <div className="mt-3">
          <Label className="text-xs">Periodicidade</Label>
          <div className="grid grid-cols-3 gap-2 mt-1" data-testid="plan-periodicity">
            {[
              { v: "weekly", label: "Semanal" },
              { v: "biweekly", label: "Quinzenal" },
              { v: "monthly", label: "Mensal" },
            ].map(opt => (
              <button
                key={opt.v} type="button" onClick={()=>setPeriodicity(opt.v)}
                className={`text-xs py-1.5 rounded-md border ${periodicity === opt.v ? "bg-emerald-600 text-white border-emerald-600" : "border-[#E5E5E5] dark:border-white/10"}`}
                data-testid={`plan-period-${opt.v}`}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        {kind === "equal" ? (
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs">Quantidade de parcelas</Label>
              <Input
                type="number" min={1} max={12} inputMode="numeric"
                value={countStr}
                onChange={e => {
                  // Permite o usuário apagar e digitar livre (sem travar no 1).
                  // Só converte ao final (no blur ou no submit).
                  const v = e.target.value;
                  setCountStr(v);
                  const n = parseInt(v, 10);
                  if (!Number.isNaN(n) && n >= 1 && n <= 12) setCount(n);
                }}
                onBlur={() => {
                  // Normaliza: vazio → 1; fora de range → clamp
                  let n = parseInt(countStr, 10);
                  if (Number.isNaN(n) || n < 1) n = 1;
                  if (n > 12) n = 12;
                  setCount(n);
                  setCountStr(String(n));
                }}
                data-testid="plan-equal-count"
              />
            </div>
            <div>
              <Label className="text-xs">Primeira data</Label>
              <Input
                type="date" value={firstDate}
                onChange={e => setFirstDate(e.target.value)}
                data-testid="plan-equal-first-date"
              />
            </div>
          </div>
        ) : (
          <div className="mt-3">
            <Label className="text-xs">Quantidade ({count}/12)</Label>
          </div>
        )}

        {/* Lista preview */}
        <div className="border border-[#E5E5E5] dark:border-white/10 rounded-md mt-3" data-testid="plan-preview">
          <div className="grid grid-cols-[1fr,1fr,40px] text-[10px] uppercase tracking-widest text-neutral-500 px-3 py-1.5 border-b border-[#E5E5E5] dark:border-white/10">
            <div>Vencimento</div><div>Valor</div><div></div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {finalList.map((p, idx) => (
              <div key={idx} className="grid grid-cols-[1fr,1fr,40px] gap-2 items-center px-3 py-2 border-b border-[#F5F5F5] dark:border-white/5 last:border-0" data-testid={`plan-row-${idx}`}>
                <Input
                  type="date" value={p.due_date}
                  onChange={e => kind === "custom" ? updateCustom(idx, { due_date: e.target.value }) : null}
                  className="h-8 text-xs"
                  disabled={kind === "equal"}
                  data-testid={`plan-row-date-${idx}`}
                />
                <CurrencyInput
                  value={p.amount}
                  onChange={(v) => kind === "custom" ? updateCustom(idx, { amount: v }) : null}
                  disabled={kind === "equal"}
                  data-testid={`plan-row-amount-${idx}`}
                />
                {kind === "custom" && customList.length > 1 && (
                  <button
                    type="button" onClick={() => removeCustomLine(idx)}
                    className="text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded p-1.5"
                    title="Remover" data-testid={`plan-row-remove-${idx}`}
                  ><Trash2 size={12}/></button>
                )}
              </div>
            ))}
          </div>
          {kind === "custom" && customList.length < 12 && (
            <button
              type="button" onClick={addCustomLine}
              className="w-full text-xs text-emerald-700 dark:text-emerald-400 py-2 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 border-t border-[#E5E5E5] dark:border-white/10 inline-flex items-center justify-center gap-1"
              data-testid="plan-row-add"
            ><Plus size={12}/> Adicionar parcela</button>
          )}
          <div className={`flex justify-between text-[11px] px-3 py-2 border-t border-[#E5E5E5] dark:border-white/10 ${sumOk ? "text-emerald-700 dark:text-emerald-400" : "text-red-600"}`} data-testid="plan-sum">
            <span>Soma das parcelas</span>
            <span>{brl(sumAmount)} / {brl(totalAmount)}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={saving || !sumOk}
                  className="bg-[#0A0A0A] text-white hover:bg-black"
                  data-testid="plan-submit">
            {saving ? "Salvando…" : initialPlan ? "Salvar alterações" : "Confirmar parcelamento"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
